import React from 'react';
// The rules page's long-read layout, deliberately: both are reference pages somebody reads
// rather than a screen they act on, and two looks for one kind of page is a look nobody chose.
import '../features/rules/rules.css';

/**
 * What the site keeps about a player, why, who else handles it, and how to have it removed
 * (lmsbe backlog item 48).
 *
 * Every claim here was checked against the code when it was written, and each is the kind
 * that goes quietly false when the code moves on. The ones most likely to:
 *
 * - **What sign-up asks for.** The fields of `RegisterPage` and the backend's `Player`. A new
 *   field there is a new line here.
 * - **"Never used for advertising", "no advertising cookies".** `AdSlot` shows the site's own
 *   house adverts (item 52), which load nothing from anywhere else and set no cookie, so both
 *   stay true. This page changes **before** an ad network goes in, not after.
 * - **The companies.** Resend, Hetzner and Cloudflare are the hosting decision in
 *   `lmsbe/docs/hosting-direction.md`. Nobody can read this page until the site is deployed
 *   on them, which is when the list becomes true; a change of host is a change here.
 * - **Who is responsible.** Phil Moran, until a company is set up to take that role.
 *
 * Plain English, short sentences, and the outcome rather than the mechanism - the same rule
 * the rules page follows, for the same reason: a player can act on "we delete it within a
 * month", and cannot act on which table it lives in.
 */
const CONTACT_EMAIL = 'help@gafferpicks.com';

function Section({ id, title, children }) {
  return (
    // Named by its heading, so a screen reader can jump between sections by name.
    <section className="rp-section" id={id} aria-labelledby={`${id}-heading`}>
      <h2 className="rp-h2" id={`${id}-heading`}>{title}</h2>
      {children}
    </section>
  );
}

function Email() {
  return <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
}

function PrivacyPage() {
  return (
    <div className="page-container fade-in">
      <div className="rp">
        <header className="rp-hero">
          <span className="rp-eyebrow">Privacy</span>
          <h1 className="rp-title">What we keep, and why.</h1>
          <p className="rp-lede">
            We keep as little about you as we can and still run the game. This page says
            exactly what, why, who else sees it, and how to have it removed.
          </p>
        </header>

        <Section id="who" title="Who we are">
          <p className="rp-body">
            Gaffer Picks is run by <strong>Phil Moran</strong>, who is responsible for the
            information on this page.
          </p>
          <p className="rp-body">
            For anything about your data &mdash; a question, a copy of what we hold, or having it
            removed &mdash; email <Email />.
          </p>
        </Section>

        <Section id="signup" title="What we keep when you sign up">
          <ul className="rp-list">
            <li><strong>Your first name and surname.</strong> Other players never see them.</li>
            <li><strong>Your email address</strong>, to sign you in and to send the emails you
              choose to get.</li>
            <li><strong>Your display name</strong>, which is what other players see.</li>
            <li><strong>Your date of birth</strong>, to check you are 18 or over.</li>
            <li><strong>Your password</strong>, stored scrambled so that nobody, including us,
              can read it.</li>
          </ul>
          <p className="rp-body">We do not ask for your address or your phone number.</p>
        </Section>

        <Section id="playing" title="What we keep while you play">
          <ul className="rp-list">
            <li>Your picks, and how each one turned out.</li>
            <li>What you post on the Banterboard.</li>
            <li>The games and leagues you are in, and any invitations or requests to join.</li>
            <li>Which emails you want from us.</li>
            <li>A record of every email we send you, or decide not to, so we can answer
              "why didn't I get that email?"</li>
            <li>If you run a game or league and do something like cancel it, what was done,
              when, and the internet address it was done from, so we can sort out any
              dispute about it.</li>
            <li>Password reset links, which expire and are deleted every night.</li>
          </ul>
        </Section>

        <Section id="why" title="Why we are allowed to keep it">
          <p className="rp-body">
            We use your details to run the game you signed up for. The records of emails and
            of what managers did are kept because we have a fair reason to: running the game
            properly and answering questions about it. We never sell your data, and we never
            use it for advertising.
          </p>
        </Section>

        <Section id="others" title="Who else can see it">
          <p className="rp-body">
            <strong>Other players</strong> see your display name, your picks once a round has
            locked, and your Banterboard posts. They do not see your real name, your email or
            your date of birth.
          </p>
          <p className="rp-body">
            A few companies run parts of the site for us, and only handle your data to do that
            job:
          </p>
          <ul className="rp-list">
            <li><strong>Resend</strong> sends our emails, so it handles your email address and
              what we send you.</li>
            <li><strong>Hetzner</strong> provides the server your details are stored on.</li>
            <li><strong>Cloudflare</strong> sits in front of the site to keep it fast and safe,
              so it sees your internet address the way any website host does. It also stores
              our nightly backups.</li>
          </ul>
        </Section>

        <Section id="cookies" title="Cookies">
          <p className="rp-body">
            We use <strong>one cookie</strong>, to keep you signed in. The site cannot work
            without it, which is why we do not ask about it. Your browser also remembers how
            you like the pick screen laid out, and that stays on your device.
          </p>
          <p className="rp-body">
            There are no analytics or advertising cookies. If that ever changes, this page will
            change first.
          </p>
        </Section>

        <Section id="how-long" title="How long we keep it">
          <p className="rp-body">
            For as long as you have an account. Ask us to remove it by emailing <Email /> and we
            will delete your account and your personal details within a month.
          </p>
        </Section>

        <Section id="rights" title="Your rights">
          <p className="rp-body">
            You can ask for a copy of what we hold about you, ask us to correct it, or ask us to
            delete it. Email <Email />.
          </p>
          <p className="rp-body">
            If you are unhappy with how we have handled your data, you can complain to the
            Information Commissioner's Office at{' '}
            <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer">
              ico.org.uk
            </a>.
          </p>
        </Section>

        <Section id="age" title="Adults only">
          <p className="rp-body">
            Gaffer Picks is for people aged 18 and over. If we find an account belonging to
            somebody younger, we close it and delete their details.
          </p>
        </Section>

        <p className="rp-body rp-body-quiet">Last updated 21 October 2026.</p>
      </div>
    </div>
  );
}

export default PrivacyPage;
